import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  ArrowLeft, Clock, Calendar, Tag, User,
  Share2, BookOpen, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import Header from '../Header';
import Footer from '../Footer';
import BlogCTA from './BlogCTA';
import BlogRating from './BlogRating';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Markdown renderer config ──────────────────────────────────────────────────
function buildComponents(onOpenWizard, ctaInserted) {
  let h2Count = 0;

  return {
    // Code blocks — dark syntax highlighting
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      if (!inline && match) {
        return (
          <SyntaxHighlighter
            style={vscDarkPlus}
            language={match[1]}
            PreTag="div"
            className="rounded-xl !bg-[#1e1e2e] border border-border/40 !text-sm !my-6"
            showLineNumbers
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        );
      }
      return (
        <code
          className="px-1.5 py-0.5 rounded-md bg-primary/10 text-primary font-mono text-[0.85em] border border-primary/20"
          {...props}
        >
          {children}
        </code>
      );
    },

    // Insert BlogCTA after the 2nd H2
    h2({ children }) {
      h2Count++;
      const el = (
        <h2 className="text-2xl font-bold text-foreground mt-10 mb-4 leading-snug">
          {children}
        </h2>
      );
      if (h2Count === 2 && !ctaInserted.current) {
        ctaInserted.current = true;
        return (
          <>
            {el}
            <BlogCTA onOpenWizard={onOpenWizard} variant="compact" />
          </>
        );
      }
      return el;
    },

    h3({ children }) {
      return <h3 className="text-xl font-bold text-foreground mt-8 mb-3">{children}</h3>;
    },

    p({ children }) {
      return <p className="text-[1rem] leading-[1.85] text-foreground/85 mb-5">{children}</p>;
    },

    a({ href, children }) {
      return (
        <a
          href={href}
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel="noopener noreferrer"
          className="text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors"
        >
          {children}
        </a>
      );
    },

    ul({ children }) {
      return <ul className="space-y-2 my-5 pl-5 list-none">{children}</ul>;
    },

    li({ children }) {
      return (
        <li className="flex items-start gap-2.5 text-foreground/80 text-[0.97rem]">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
          <span>{children}</span>
        </li>
      );
    },

    ol({ children }) {
      return <ol className="space-y-2 my-5 pl-1 list-decimal list-inside">{children}</ol>;
    },

    blockquote({ children }) {
      return (
        <blockquote className="my-6 pl-5 border-l-4 border-primary/60 bg-primary/5 rounded-r-xl py-3 pr-4 italic text-foreground/70">
          {children}
        </blockquote>
      );
    },

    table({ children }) {
      return (
        <div className="my-6 overflow-x-auto rounded-xl border border-border/40">
          <table className="w-full text-sm">{children}</table>
        </div>
      );
    },

    thead({ children }) {
      return <thead className="bg-card/80">{children}</thead>;
    },

    th({ children }) {
      return <th className="px-4 py-3 text-left font-semibold text-foreground border-b border-border/40">{children}</th>;
    },

    td({ children }) {
      return <td className="px-4 py-3 text-foreground/80 border-b border-border/20">{children}</td>;
    },

    hr() {
      return <hr className="my-8 border-border/30" />;
    },

    strong({ children }) {
      return <strong className="font-bold text-foreground">{children}</strong>;
    },
  };
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ post, onOpenWizard }) {
  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: post.title, url: window.location.href });
    } else {
      await navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <aside className="space-y-5 lg:sticky lg:top-28">
      {/* Author card */}
      <div className="glass rounded-2xl p-5 border border-border/30">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-base flex-shrink-0">
            {post.author?.name?.slice(0, 2).toUpperCase() || 'ZB'}
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{post.author?.name}</p>
            <p className="text-xs text-muted-foreground">{post.author?.role}</p>
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t border-border/30">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="w-3.5 h-3.5 text-primary flex-shrink-0" />
            {new Date(post.published_date).toLocaleDateString('fr-FR', {
              day: 'numeric', month: 'long', year: 'numeric'
            })}
          </div>
          {post.reading_time_minutes && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              {post.reading_time_minutes} min de lecture
            </div>
          )}
          {post.view_count > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <BookOpen className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              {post.view_count.toLocaleString('fr-FR')} lectures
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      {post.tags?.length > 0 && (
        <div className="glass rounded-2xl p-5 border border-border/30">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Thèmes</p>
          <div className="flex flex-wrap gap-2">
            {post.tags.map(tag => (
              <Link
                key={tag}
                to={`/blog?tag=${encodeURIComponent(tag)}`}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold
                  bg-primary/10 border border-primary/25 text-primary
                  hover:bg-primary/20 transition-colors"
              >
                <Tag className="w-3 h-3" />
                {tag}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Share */}
      <button
        onClick={handleShare}
        className="w-full glass rounded-xl p-4 border border-border/30 hover:border-primary/30
          flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground
          transition-all duration-200 group"
      >
        <Share2 className="w-4 h-4 text-primary group-hover:rotate-12 transition-transform" />
        Partager cet article
      </button>

      {/* Sidebar CTA mini */}
      <div className="glass rounded-2xl p-5 border border-primary/20 bg-gradient-to-br from-primary/8 to-secondary/5">
        <p className="text-sm font-bold text-foreground mb-2">
          Prêt à migrer ?
        </p>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Obtenez votre score de maturité IAM et un rapport personnalisé en 3 min.
        </p>
        <Button
          onClick={onOpenWizard}
          size="sm"
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground glow-purple text-xs"
        >
          Diagnostic gratuit
          <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
    </aside>
  );
}

// ── Main BlogPost ─────────────────────────────────────────────────────────────
export default function BlogPost({ onOpenWizard }) {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ctaInserted = { current: false };

  useEffect(() => {
    setLoading(true);
    setPost(null);
    ctaInserted.current = false;

    fetch(`${API_BASE}/api/blog/posts/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? 'Article introuvable.' : 'Erreur réseau.');
        return r.json();
      })
      .then(data => {
        setPost(data.post);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  const components = post
    ? buildComponents(onOpenWizard, ctaInserted)
    : {};

  return (
    <>
      {post && (
        <Helmet>
          <title>{post.seo_title || post.title} | Auth0.Expert</title>
          <meta name="description" content={post.seo_description || post.excerpt} />
          <link rel="canonical" href={`https://auth0.expert/blog/${post.slug || slug}`} />
          <meta property="og:title" content={post.seo_title || post.title} />
          <meta property="og:description" content={post.seo_description || post.excerpt} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={`https://auth0.expert/blog/${post.slug || slug}`} />
          <meta property="article:published_time" content={post.published_date} />
          {post.tags?.map(t => <meta key={t} property="article:tag" content={t} />)}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={post.seo_title || post.title} />
          <meta name="twitter:description" content={post.seo_description || post.excerpt} />
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.seo_title || post.title,
            description: post.seo_description || post.excerpt,
            datePublished: post.published_date,
            author: { '@type': 'Organization', name: post.author || 'Auth0.Expert' },
            publisher: { '@type': 'Organization', name: 'Auth0.Expert', url: 'https://auth0.expert/' },
            mainEntityOfPage: `https://auth0.expert/blog/${post.slug || slug}`,
            keywords: (post.tags || []).join(', '),
          })}</script>
        </Helmet>
      )}

      <div className="min-h-screen bg-background grid-bg">
        <Header scrolled onCTAClick={onOpenWizard} />

        {/* Decorative orbs */}
        <div className="pointer-events-none fixed top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute bottom-40 right-1/4 w-72 h-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <main className="max-w-6xl mx-auto px-4 pt-28 pb-20">
          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-32 gap-4">
              <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
              <p className="text-muted-foreground text-sm">Chargement de l'article...</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="text-center py-32">
              <p className="text-xl font-bold text-foreground mb-2">{error}</p>
              <p className="text-muted-foreground mb-6">
                Cet article n'existe pas ou n'est pas encore publié.
              </p>
              <Link to="/blog">
                <Button variant="outline" className="border-border">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour au blog
                </Button>
              </Link>
            </div>
          )}

          {/* Article */}
          {!loading && !error && post && (
            <>
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
                <Link to="/" className="hover:text-foreground transition-colors">Accueil</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-foreground/70 truncate max-w-[200px]">{post.title}</span>
              </nav>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12 items-start">

                {/* ── Article content ── */}
                <article>
                  {/* Header */}
                  <header className="mb-8">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags?.map(tag => (
                        <Link
                          key={tag}
                          to={`/blog?tag=${encodeURIComponent(tag)}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
                            bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 transition-colors"
                        >
                          <Tag className="w-3 h-3" />
                          {tag}
                        </Link>
                      ))}
                    </div>

                    {/* Title */}
                    <h1 className="text-3xl md:text-4xl font-bold text-foreground leading-tight mb-4">
                      {post.title}
                    </h1>

                    {/* Excerpt */}
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6 border-l-4 border-primary/40 pl-4">
                      {post.excerpt}
                    </p>

                    {/* Meta bar */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pb-6 border-b border-border/30">
                      <span className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-primary" />
                        <span className="font-medium text-foreground/80">{post.author?.name}</span>
                        <span className="text-xs text-muted-foreground/70">— {post.author?.role}</span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-primary" />
                        {new Date(post.published_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                      {post.reading_time_minutes && (
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-primary" />
                          {post.reading_time_minutes} min de lecture
                        </span>
                      )}
                    </div>
                  </header>

                  {/* Cover image */}
                  {post.cover_image && (
                    <div className="mb-8 rounded-2xl overflow-hidden border border-border/30">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-64 md:h-80 object-cover"
                      />
                    </div>
                  )}

                  {/* Markdown content */}
                  <div className="prose-content">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={components}
                    >
                      {post.content_markdown || post.content || ''}
                    </ReactMarkdown>
                  </div>

                  {/* Rating widget */}
                  <BlogRating slug={post.slug} />

                  {/* End-of-article CTA */}
                  <BlogCTA onOpenWizard={onOpenWizard} />

                  {/* Back link */}
                  <div className="mt-10 pt-6 border-t border-border/30">
                    <Link
                      to="/blog"
                      className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Voir tous les articles
                    </Link>
                  </div>
                </article>

                {/* ── Sidebar ── */}
                <Sidebar post={post} onOpenWizard={onOpenWizard} />
              </div>
            </>
          )}
        </main>

        <Footer onCTAClick={onOpenWizard} />
      </div>
    </>
  );
}
